import { CommonUtility } from '@utility/common';
import { type ReactNode, useMemo } from 'react';
import { Badge, Button, cn, Popover } from '@skupreme/shared/ui';
import { ItemImage } from '@components/CatalogElements';
import { Info, MapPin, Package, Warning } from '@phosphor-icons/react';
import { DarkPopup } from '@elements/Popup';
import { OrderStatus, tooltipTexts } from '@utility/constants/common';
import { Flag } from '@elements/Flag';
import { Link } from 'react-router';
import type { OrderItemFulfilmentDetails } from '@skupreme/api-contracts/core';
import { toPlacement } from '@utility/popup-placement';
import {
  getProviderAvailableForOrderItem,
  getUnfulfillablePendingShipmentItemCount,
  getUnfulfilledOrderItemQty,
} from '@features/orders';

/* Provider and item shapes as the order and batch lists hand them over. */
type ProviderItem = {
  _id?: string;
  item_id?: string;
  title?: string;
  sku?: string;
  qty?: number;
  qty_to_fulfill?: number;
  available?: number;
  item_price?: number;
  p_images?: unknown;
};

type FulfillmentProvider = {
  _id?: string;
  name?: string;
  country?: string;
  integration_type?: string;
  wmsOrganizationLogo?: { url?: string };
  address?: { city?: string; state?: string };
  items?: ProviderItem[];
};

type OrderItem = {
  _id?: string;
  item_id?: string;
  title?: string;
  sku?: string;
  qty?: number;
  amount?: number;
  state?: string;
  p_images?: unknown;
  fulfilment_details?: OrderItemFulfilmentDetails[];
};

/** A row's popovers sit inside a clickable table row; clicks on them must not reach it. */
function StopRowClick({ children }: { children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: only keeps clicks from reaching the row; the popover trigger inside is the interactive element.
    <span className="sk:inline-flex" onClick={e => e.stopPropagation()}>
      {children}
    </span>
  );
}

const fpIcon = (type?: string) => {
  switch (type) {
    case 'manual':
      return '/images/fp/manual.svg';
    case 'manualDark':
      return '/images/fp/manualDark.svg';
    case 'spapi':
      return '/images/fp/fba.png';
    case 'deliverr':
      return '/images/fp/deliverr.png';
    case 'shiphero':
      return '/images/fp/shiphero.png';
    case '3pl':
      return '/images/fp/3pl-grey.svg';
    case 'tactical3pl':
      return '/images/fp/tactical.svg';
    default:
      return '/images/fp/3pl-grey.svg';
  }
};

function Initial({ children }: { children: ReactNode }) {
  return (
    <span className="sk:flex sk:size-full sk:items-center sk:justify-center sk:rounded-full sk:font-bold sk:uppercase sk:inset-shadow-hairline">
      {children}
    </span>
  );
}

function ProviderItemRow({ item }: { item: ProviderItem }) {
  return (
    <li
      id={item._id}
      className="sk:grid sk:grid-cols-[40px_minmax(0,1fr)_auto_auto] sk:items-center sk:gap-3 sk:py-1.5"
    >
      <ItemImage item={item} size="mini" noMargin />
      <div className="sk:min-w-0">
        <p className="sk:m-0 sk:truncate">{item.title}</p>
        <p className="sk:m-0 sk:truncate sk:font-semibold">{item.sku}</p>
      </div>
      <span className="sk:text-center">
        x
        {item.available
          ? Math.min(item.qty_to_fulfill || item.qty || 0, item.available || 0)
          : item.qty}
      </span>
      <span className="sk:text-right">
        {CommonUtility.currencyFormat(item.item_price)}
      </span>
    </li>
  );
}

function ProviderRow({ provider }: { provider: FulfillmentProvider }) {
  const icon = fpIcon(provider.integration_type);
  const initial = provider?.name ? provider.name[0] : '';

  return (
    <div className="sk:py-2">
      <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-2">
        <span className="sk:flex sk:size-5 sk:shrink-0 sk:items-center sk:justify-center">
          {icon ? (
            <img src={icon} alt="" className="sk:size-5 sk:object-contain" />
          ) : (
            <Initial>{initial}</Initial>
          )}
        </span>
        <span className="sk:flex sk:items-center sk:gap-1 sk:font-semibold">
          {provider?.country && <Flag name={provider.country} />}
          {provider.name}
        </span>
        <span className="sk:flex sk:items-center sk:gap-1 sk:text-fg-tertiary">
          <MapPin size={12} />
          {provider?.address?.city}, {provider?.address?.state}
        </span>
      </div>
      <ul className="sk:m-0 sk:mt-1 sk:list-none sk:border-0 sk:border-l sk:border-dashed sk:border-border sk:py-0 sk:pr-0 sk:pl-4">
        {(provider.items || []).map(item => (
          <ProviderItemRow item={item} key={item._id} />
        ))}
        {(provider.items || []).length === 0 && (
          <li className="sk:py-1.5 sk:text-fg-tertiary">No Items found</li>
        )}
      </ul>
    </div>
  );
}

export function SingleCurrentFP({
  currentFP,
}: {
  currentFP?: FulfillmentProvider | null;
}) {
  const { icon, initial } = useMemo(() => {
    const type =
      currentFP?.integration_type === 'manual'
        ? 'manualDark'
        : currentFP?.integration_type;
    const icon = currentFP?.wmsOrganizationLogo?.url || fpIcon(type);
    return {
      icon,
      initial: currentFP?.name ? currentFP?.name[0] : '',
    };
  }, [currentFP]);

  return (
    <div
      className="sk:flex sk:items-center sk:gap-1.5 sk:whitespace-nowrap"
      title={currentFP?.name}
    >
      <span className="sk:flex sk:size-9 sk:shrink-0 sk:items-center sk:justify-center sk:rounded-full sk:bg-fill-tertiary sk:p-1.5">
        {icon ? (
          <img src={icon} alt="" className="sk:w-5 sk:object-contain" />
        ) : (
          <Initial>{initial}</Initial>
        )}
      </span>
      <div className="sk:min-w-0">
        <div className="sk:truncate">{currentFP?.name}</div>
        {(currentFP?.address?.city || currentFP?.address?.state) && (
          <span className="sk:flex sk:items-center sk:gap-1 sk:text-fg-tertiary">
            <MapPin size={12} />
            {currentFP?.address?.city}, {currentFP?.address?.state}
          </span>
        )}
      </div>
    </div>
  );
}

function ProviderOOSLabel({
  totalItems,
  itemsNotInPendingFulfillmentState,
}: {
  totalItems?: number;
  itemsNotInPendingFulfillmentState: number;
}) {
  return (
    <DarkPopup
      trigger={
        <Badge tone="danger">
          <Warning />
          {itemsNotInPendingFulfillmentState} / {totalItems} Items OOS
          <Info />
        </Badge>
      }
      position="top center"
    >
      {totalItems === itemsNotInPendingFulfillmentState
        ? 'All items '
        : `${itemsNotInPendingFulfillmentState} / ${totalItems} Items `}
      {tooltipTexts.orderOOS}
    </DarkPopup>
  );
}

type FPInfoPopupProps = {
  providers?: FulfillmentProvider[] | null;
  selectedFP?: FulfillmentProvider | null;
  isShipped?: boolean;
  totalItems?: number;
  orderState?: string;
  items?: OrderItem[];
  highlightOOS?: boolean;
  /** Older name some callers pass; the count is worked out here. */
  itemsNotInPendingFulfillmentState?: number;
};

export function FPInfoPopup({
  providers: providersList,
  selectedFP,
  isShipped,
  totalItems,
  orderState,
  items,
  highlightOOS = false,
}: FPInfoPopupProps) {
  const providers = CommonUtility.isValidArray(providersList)
    ? providersList
    : [];

  const currentFP = useMemo(() => {
    if (providers.length !== 1) return null;

    return selectedFP?._id && providers[0]._id !== selectedFP?._id
      ? selectedFP
      : providers[0];
  }, [providers, selectedFP]);

  const itemsThatCannotBeFulfilled = useMemo(() => {
    return getUnfulfillablePendingShipmentItemCount(
      items,
      currentFP ?? undefined,
    );
  }, [currentFP, items]);

  if (providers.length === 0 && !isShipped) {
    if (orderState !== OrderStatus.PendingShipment) {
      return (
        <Badge tone="danger">
          <Warning /> Provider Unassigned
        </Badge>
      );
    }

    return (
      <ProviderOOSLabel
        totalItems={totalItems}
        itemsNotInPendingFulfillmentState={itemsThatCannotBeFulfilled}
      />
    );
  }

  if (providers.length > 1) {
    return (
      <StopRowClick>
        <Popover
          aria-label="Fulfillment providers"
          placement="top"
          className="sk:w-112 sk:max-w-[calc(100vw-32px)]"
          trigger={
            <Button variant="ghost" size="sm">
              <Package />
              <span className="sk:underline">Multiple</span>
              <Info className="sk:text-fg-tertiary" />
            </Button>
          }
        >
          <div className="sk:max-h-96 sk:overflow-y-auto sk:px-2">
            {providers.map(provider => (
              <ProviderRow key={provider._id} provider={provider} />
            ))}
          </div>
        </Popover>
      </StopRowClick>
    );
  }

  if (currentFP) {
    return (
      <div className="sk:flex sk:flex-col sk:items-start sk:gap-2">
        <SingleCurrentFP currentFP={currentFP} />
        {!!itemsThatCannotBeFulfilled && highlightOOS && (
          <ProviderOOSLabel
            totalItems={totalItems}
            itemsNotInPendingFulfillmentState={itemsThatCannotBeFulfilled}
          />
        )}
      </div>
    );
  }

  return null;
}

function ItemBoxItemRow({
  itemsWithProvider,
  item,
  highlightOOS,
}: {
  itemsWithProvider: ProviderItem[];
  item: OrderItem;
  highlightOOS: boolean;
}) {
  const unfulfilledQty = useMemo(
    () => getUnfulfilledOrderItemQty(item),
    [item],
  );

  const itemsThatWillBeLeft = useMemo(() => {
    if (!highlightOOS) return 0;

    return (
      unfulfilledQty - getProviderAvailableForOrderItem(itemsWithProvider, item)
    );
  }, [highlightOOS, itemsWithProvider, item, unfulfilledQty]);

  const itemsThatWillBeFulfilled = useMemo(() => {
    if (!highlightOOS) return unfulfilledQty;

    const left = itemsThatWillBeLeft > 0 ? itemsThatWillBeLeft : 0;

    return unfulfilledQty - left;
  }, [highlightOOS, unfulfilledQty, itemsThatWillBeLeft]);

  const showNumbers = useMemo(
    () => itemsThatWillBeFulfilled > 0 && itemsThatWillBeLeft > 0,
    [itemsThatWillBeFulfilled, itemsThatWillBeLeft],
  );

  const unitTerm = (item.qty ?? 0) > 1 ? 'Units' : 'Unit';

  return (
    <div className={ITEM_GRID}>
      <ItemImage item={item} size="mini" noMargin />
      <div className="sk:min-w-0">
        <div className="sk:truncate">{item.title}</div>
        <Link
          to={`/app/catalogs/catalog/${item.item_id}/?tab=package-attributes`}
          className="sk:block sk:truncate sk:font-semibold sk:text-fg-secondary sk:underline"
        >
          {item.sku}
        </Link>
      </div>
      <span>x{item.qty}</span>
      <div className="sk:flex sk:flex-col sk:items-center sk:gap-1">
        {highlightOOS ? (
          itemsThatWillBeLeft > 0 && (
            <Badge tone="danger">
              {showNumbers ? `${itemsThatWillBeLeft}x ` : ''}
              Out of Stock
            </Badge>
          )
        ) : (
          <span>
            {CommonUtility.numberWithCommas(item.qty)}{' '}
            <span className="sk:text-fg-tertiary">{unitTerm}</span>
          </span>
        )}
        {itemsThatWillBeFulfilled > 0 && (
          <Badge tone="success">
            {showNumbers ? `${itemsThatWillBeFulfilled}x ` : ''}
            {item.state}
          </Badge>
        )}
      </div>
      <span className="sk:text-right">
        {CommonUtility.currencyFormat(item.amount)}
      </span>
    </div>
  );
}

const ITEM_GRID =
  'sk:grid sk:grid-cols-[40px_minmax(0,1fr)_56px_minmax(0,140px)_72px] sk:items-center sk:gap-3';

function ItemBox({
  items,
  itemsWithProvider,
  highlightOOS,
}: {
  items?: OrderItem[];
  itemsWithProvider: ProviderItem[];
  highlightOOS: boolean;
}) {
  return (
    <div className="sk:flex sk:max-h-100 sk:min-w-130 sk:flex-col sk:gap-2 sk:overflow-auto sk:p-2 sk:text-xs">
      <div className={cn(ITEM_GRID, 'sk:text-fg-tertiary')}>
        <span className="sk:col-span-2">Product Information</span>
        <span>Quantity</span>
        <span className="sk:text-center">Status</span>
        <span className="sk:text-right">Total</span>
      </div>
      {(items || []).map(item => (
        <ItemBoxItemRow
          key={item.item_id}
          item={item}
          itemsWithProvider={itemsWithProvider}
          highlightOOS={
            highlightOOS && item.state === OrderStatus.PendingShipment
          }
        />
      ))}
      {highlightOOS && (
        <span className="sk:mt-2 sk:text-fg-tertiary">
          * The above fulfillment data is based on currently selected
          fulfillment provider.
        </span>
      )}
    </div>
  );
}

type ItemInfoPopupProps = {
  total_items?: number;
  items: OrderItem[];
  /** A Semantic position such as 'top center'. */
  position?: string;
  providers?: FulfillmentProvider[] | null;
  selectedFP?: FulfillmentProvider | null;
  highlightOOS?: boolean;
};

export function ItemInfoPopup({
  total_items,
  items,
  position,
  providers: providersList,
  selectedFP,
  highlightOOS = false,
}: ItemInfoPopupProps) {
  const providers = CommonUtility.isValidArray(providersList)
    ? providersList
    : [];

  const currentFP = useMemo(() => {
    if (!CommonUtility.isValidArray(providers)) return null;

    return selectedFP?._id && providers[0]._id !== selectedFP?._id
      ? selectedFP
      : providers[0];
  }, [providers, selectedFP]);

  return (
    <StopRowClick>
      <Popover
        aria-label="Order items"
        placement={toPlacement(position)}
        className="sk:max-w-[calc(100vw-32px)] sk:overflow-x-auto"
        trigger={
          <Button variant="ghost" size="sm" className="sk:px-1.5">
            {CommonUtility.numberWithCommas(items.length)}
            <span className="sk:text-fg-tertiary">
              ({CommonUtility.numberWithCommas(total_items)} unit
              {(total_items ?? 0) > 1 ? 's' : ''})
            </span>
            <Info />
          </Button>
        }
      >
        <ItemBox
          items={items}
          highlightOOS={highlightOOS}
          itemsWithProvider={currentFP?.items || []}
        />
      </Popover>
    </StopRowClick>
  );
}
